import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckBox, NumberBox, TagBox, TextArea } from 'devextreme-react';
import { ScrollView } from 'devextreme-react/scroll-view';
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { useApiMicrobiology } from '../../../api/api.microbiology';
import { SysI_MicrobiologiaConfiguracion, SysI_MicrobiologiaAntibioticosCatalogo, SysI_MicrobiologiaMicroorganismosCatalogo } from '../../../types/types.microbiology';
import { newToast } from '../../../components';

interface MicrobiologyConfigurationProps {
  IdAnalito: number;
  Analito: string;
}

export const MicrobiologyConfiguration: React.FC<MicrobiologyConfigurationProps> = ({ IdAnalito, Analito }) => {
  const { getConfiguracionAnalito, saveConfiguracionAnalito, getAntibioticos, getMicroorganismos } = useApiMicrobiology();

  const [loading, setLoading] = useState(false);
  const [antibioticos, setAntibioticos] = useState<SysI_MicrobiologiaAntibioticosCatalogo[]>([]);
  const [microorganismos, setMicroorganismos] = useState<SysI_MicrobiologiaMicroorganismosCatalogo[]>([]);

  const [formData, setFormData] = useState<SysI_MicrobiologiaConfiguracion>({
    Mcf_An_Id: IdAnalito,
    Mcf_UsarPlantilla: false,
    Mcf_HabilitarGram: true,
    Mcf_PlantillaGram: '',
    Mcf_HabilitarCultivo: true,
    Mcf_HabilitarIdentificacion: true,
    Mcf_HabilitarAntibiograma: true,
    Mcf_ReportarGramSiNegativo: false,
    Mcf_ReportarCultivoNegativoInmediato: false,
    Mcf_TiempoReportePreliminar1: 0,
    Mcf_TiempoReportePreliminar2: 0,
    Mcf_TiempoReporteFinal: 0,
    Mcf_PanelAntibioticosGramPositivos: '[]',
    Mcf_PanelAntibioticosGramNegativos: '[]',
  });

  // Cargar datos iniciales
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [config, antibioticosData, microorganismosData] = await Promise.all([getConfiguracionAnalito(IdAnalito), getAntibioticos(), getMicroorganismos()]);

        if (isMounted) {
          setAntibioticos(antibioticosData || []);
          setMicroorganismos(microorganismosData || []);

          if (config) {
            setFormData({
              ...config,
              Mcf_PanelAntibioticosGramPositivos: config.Mcf_PanelAntibioticosGramPositivos || '[]',
              Mcf_PanelAntibioticosGramNegativos: config.Mcf_PanelAntibioticosGramNegativos || '[]',
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error cargando datos:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, [IdAnalito]);

  // Lista plana de antibióticos para TagBox
  const antibioticosOptions = useMemo(() => {
    if (!antibioticos || antibioticos.length === 0) return [];

    return antibioticos
      .filter((ab) => ab.Abc_Estado === true)
      .sort((a, b) => (a.Abc_OrdenPresentacion || 0) - (b.Abc_OrdenPresentacion || 0))
      .map((ab) => ({
        id: ab.Abc_Id,
        text: `${ab.Abc_Nombre} (${ab.Abc_FamiliaAntibiotico || 'Sin familia'})`,
        familia: ab.Abc_FamiliaAntibiotico,
      }));
  }, [antibioticos]);

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      if (formData.Mcf_UsarPlantilla && formData.Mcf_PlantillaGram) {
        try {
          JSON.parse(formData.Mcf_PlantillaGram);
        } catch (error) {
          newToast('ERROR', 'El JSON de la plantilla no es válido. Por favor corrígelo antes de guardar.', 5);
          setLoading(false);
          return;
        }
      }
      await saveConfiguracionAnalito(formData);
    } catch (error) {
      console.error('Error guardando configuración:', error);
    } finally {
      setLoading(false);
    }
  }, [formData, saveConfiguracionAnalito]);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const getPanelValue = useCallback((panelJson: string | undefined) => {
    if (!panelJson) return [];
    try {
      const parsed = JSON.parse(panelJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const setPanelValue = useCallback((field: string, value: any[]) => {
    const arrayValue = Array.isArray(value) ? value : [];
    const jsonString = JSON.stringify(arrayValue);

    // Solo actualizar si el valor realmente cambió
    setFormData((prev) => {
      const currentValue = prev[field];
      if (currentValue === jsonString) {
        return prev; // No actualizar si es el mismo valor
      }
      return {
        ...prev,
        [field]: jsonString,
      };
    });
  }, []);

  // Handler específico para TagBox
  const handleTagBoxChange = useCallback((field: string, newValue: any) => {
    const arrayValue = Array.isArray(newValue) ? newValue : [];
    const jsonString = JSON.stringify(arrayValue);

    setFormData((prev) => {
      // Comparar el valor actual con el nuevo
      if (prev[field] === jsonString) {
        return prev;
      }
      return {
        ...prev,
        [field]: jsonString,
      };
    });
  }, []);

  return (
    <div className='microbiology-configuration'>
      <div className='config-header'>
        <h3>Configuración de Microbiología</h3>
        <p className='config-subtitle'>{Analito}</p>
      </div>

      <TabPanel height={'70%'} animationEnabled swipeEnabled={false}>
        {/* PESTAÑA 1: COMPONENTES HABILITADOS */}
        <TabItem title='Componentes' icon='check'>
          <ScrollView height='100%' showScrollbar='onScroll'>
            <div className='config-section'>
              <div className='section-header'>
                <i className='dx-icon-check'></i>
                <h4>¿Qué componentes se reportarán?</h4>
              </div>

              <div className='components-grid'>
                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarGram} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarGram', e.value)} text='Examen Directo (Gram)' />
                  <p className='component-description'>Observación microscópica inicial de la muestra</p>

                  {formData.Mcf_HabilitarGram && (
                    <>
                      <div className='sub-option'>
                        <CheckBox value={formData.Mcf_ReportarGramSiNegativo} onValueChanged={(e) => handleFieldChange('Mcf_ReportarGramSiNegativo', e.value)} text='Reportar aunque sea negativo' />
                      </div>

                      {/* NUEVA OPCIÓN: Usar Plantilla */}
                      <div className='sub-option' style={{ marginTop: '12px' }}>
                        <CheckBox value={formData.Mcf_UsarPlantilla} onValueChanged={(e) => handleFieldChange('Mcf_UsarPlantilla', e.value)} text='Usar plantilla para Gram' />
                      </div>
                    </>
                  )}
                </div>

                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarCultivo} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarCultivo', e.value)} text='Cultivo' />
                  <p className='component-description'>Crecimiento en medio de cultivo</p>
                  {formData.Mcf_HabilitarCultivo && (
                    <div className='sub-option'>
                      <CheckBox value={formData.Mcf_ReportarCultivoNegativoInmediato} onValueChanged={(e) => handleFieldChange('Mcf_ReportarCultivoNegativoInmediato', e.value)} text='Reportar cultivo negativo inmediatamente' />
                    </div>
                  )}
                </div>

                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarIdentificacion} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarIdentificacion', e.value)} text='Identificación' />
                  <p className='component-description'>Identificación del microorganismo aislado</p>
                </div>
                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarAntibiograma} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarAntibiograma', e.value)} text='Antibiograma' />
                  <p className='component-description'>Prueba de sensibilidad a antibióticos</p>
                </div>
              </div>

              {/* MOSTRAR TEXTAREA SI USA PLANTILLA */}
              {formData.Mcf_UsarPlantilla && (
                <div className='compponent-grid'>
                  <div className='component-card'>
                    <div style={{ marginTop: '15px', marginLeft: '30px' }}>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#4a5568',
                        }}>
                        Plantilla JSON:
                      </label>
                      <TextArea value={formData.Mcf_PlantillaGram} onValueChanged={(e) => handleFieldChange('Mcf_PlantillaGram', e.value)} height={200} placeholder='Pegue aquí el JSON de la plantilla...' stylingMode='outlined' />
                      <p
                        style={{
                          margin: '8px 0 0 0',
                          fontSize: '12px',
                          color: '#718096',
                          fontStyle: 'italic',
                        }}>
                        Pegue el JSON exactamente como fue generado en el configurador de plantillas
                      </p>

                      {/* Botón para validar JSON */}
                      <Button
                        text='Validar JSON'
                        icon='check'
                        type='normal'
                        stylingMode='outlined'
                        onClick={() => {
                          try {
                            JSON.parse(formData.Mcf_PlantillaGram || '{}');
                            newToast('SUCCESS', 'El JSON es válido', 3);
                          } catch (error) {
                            newToast('ERROR', 'El JSON no es válido. Verifique el formato.', 5);
                          }
                        }}
                        style={{ marginTop: '10px' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollView>
        </TabItem>

        {/* PESTAÑA 2: TIEMPOS DE REPORTE */}
        <TabItem title='Tiempos' icon='clock'>
          <div className='config-section'>
            <div className='section-header'>
              <i className='dx-icon-clock'></i>
              <h4>Tiempos de reporte (en horas)</h4>
            </div>

            <div className='time-inputs'>
              <div className='time-card'>
                <label className='time-label'>
                  <i className='dx-icon-event'></i>
                  Reporte Preliminar 1
                </label>
                <NumberBox value={formData.Mcf_TiempoReportePreliminar1} onValueChanged={(e) => handleFieldChange('Mcf_TiempoReportePreliminar1', e.value)} placeholder='Ej: 18' min={0} step={1} showSpinButtons />
                <p className='time-hint'>Tiempo para el primer reporte parcial</p>
              </div>

              <div className='time-card'>
                <label className='time-label'>
                  <i className='dx-icon-event'></i>
                  Reporte Preliminar 2
                </label>
                <NumberBox value={formData.Mcf_TiempoReportePreliminar2} onValueChanged={(e) => handleFieldChange('Mcf_TiempoReportePreliminar2', e.value)} placeholder='Ej: 24' min={0} step={1} showSpinButtons />
                <p className='time-hint'>Tiempo para el segundo reporte parcial</p>
              </div>

              <div className='time-card'>
                <label className='time-label'>
                  <i className='dx-icon-check'></i>
                  Reporte Final
                </label>
                <NumberBox value={formData.Mcf_TiempoReporteFinal} onValueChanged={(e) => handleFieldChange('Mcf_TiempoReporteFinal', e.value)} placeholder='Ej: 48' min={0} step={1} showSpinButtons />
                <p className='time-hint'>Tiempo para el reporte definitivo</p>
              </div>
            </div>

            <div className='time-explanation'>
              <i className='dx-icon-info'></i>
              <p>Configure los tiempos estándar para el reporte de preliminares y finales</p>
            </div>
          </div>
        </TabItem>

        {/* PESTAÑA 3: PANELES DE ANTIBIÓTICOS */}
        <TabItem title='Antibiogramas' icon='fieldchooser'>
          <div className='config-section'>
            <div className='section-header'>
              <i className='dx-icon-fieldchooser'></i>
              <h4>Paneles predefinidos de antibióticos</h4>
            </div>

            <div className='panels-container'>
              <div className='panel-card gram-positive'>
                <div className='panel-header'>
                  <i className='dx-icon-plus'></i>
                  <h5>Panel para Gram Positivos</h5>
                </div>
                <p className='panel-description'>Seleccione los antibióticos que se mostrarán por defecto para microorganismos Gram positivos</p>
                <TagBox dataSource={antibioticosOptions} displayExpr='text' valueExpr='id' value={getPanelValue(formData.Mcf_PanelAntibioticosGramPositivos)} onValueChanged={(e) => setPanelValue('Mcf_PanelAntibioticosGramPositivos', e.value || [])} searchEnabled={true} showSelectionControls={true} applyValueMode='useButtons' placeholder='Seleccione antibióticos...' height={250} noDataText='No hay antibióticos disponibles' />
                <div className='panel-stats'>
                  <span className='stat-badge'>{getPanelValue(formData.Mcf_PanelAntibioticosGramPositivos).length} seleccionados</span>
                </div>
              </div>

              <div className='panel-card gram-negative'>
                <div className='panel-header'>
                  <i className='dx-icon-minus'></i>
                  <h5>Panel para Gram Negativos</h5>
                </div>
                <p className='panel-description'>Seleccione los antibióticos que se mostrarán por defecto para microorganismos Gram negativos</p>
                <TagBox dataSource={antibioticosOptions} displayExpr='text' valueExpr='id' value={getPanelValue(formData.Mcf_PanelAntibioticosGramNegativos)} onValueChanged={(e) => setPanelValue('Mcf_PanelAntibioticosGramNegativos', e.value || [])} searchEnabled={true} showSelectionControls={true} applyValueMode='instantly' placeholder='Seleccione antibióticos...' height={250} noDataText='No hay antibióticos disponibles' />
                <div className='panel-stats'>
                  <span className='stat-badge'>{getPanelValue(formData.Mcf_PanelAntibioticosGramNegativos).length} seleccionados</span>
                </div>
              </div>
            </div>

            <div className='panel-hint'>
              <i className='dx-icon-info'></i>
              <p>Configure los antibióticos que se reportarán para este estudio</p>
            </div>
          </div>
        </TabItem>
      </TabPanel>

      {/* BOTÓN DE GUARDAR */}
      <div className='config-actions'>
        <Button text='GUARDAR CONFIGURACIÓN' type='default' onClick={handleSave} disabled={loading} width={250} />
      </div>
    </div>
  );
};
